import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import MfaChallenge from "./MfaChallenge";
import { signInWithGoogle } from "../../application/signIn";
import { saveNewTherapistPreferences } from "../../application/signUp";
import type { Therapist } from "../../../therapist";
import Button from "../../../../shared/ui/button/Button";
import { useTherapist } from "../../../../context/TherapistProvider";

interface Props {
  mode: "signIn" | "signUp";
}

function LanguageSetupModal({ therapist, onDone }: { therapist: Therapist; onDone: (updated: Therapist) => void }) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState(therapist.language ?? "es");
  const [patientTerm, setPatientTerm] = useState(therapist.patient_term ?? "patient");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await saveNewTherapistPreferences(therapist.id, { language, patient_term: patientTerm });
      onDone(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface-raised rounded-2xl shadow-xl p-8 w-full max-w-sm mx-4 flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-bold text-content-primary">{t("profile.sessionConfig.notes")}</h2>
          <p className="mt-1 text-sm text-content-tertiary">{t("profile.sessionConfig.languageSelect")}</p>
        </div>
        <select
          className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <option value="es">Español</option>
          <option value="en">English</option>
        </select>
        <div>
          <p className="mb-2 text-sm text-content-tertiary">
            {t("profile.patientTerm.label")}
          </p>
          <select
            className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            value={patientTerm}
            onChange={(e) => setPatientTerm(e.target.value)}
          >
            <option value="patient">{t("profile.patientTerm.patient")}</option>
            <option value="client">{t("profile.patientTerm.client")}</option>
            <option value="consultee">{t("profile.patientTerm.consultee")}</option>
          </select>
        </div>
        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? t("common.saving") : t("common.continue")}
        </Button>
      </div>
    </div>
  );
}

export default function GoogleSignInButton({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") ?? undefined;
  const { setTherapist, refreshTherapist } = useTherapist();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTherapist, setNewTherapist] = useState<Therapist | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<{ email: string } | null>(null);

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await signInWithGoogle(inviteToken);
      // Google stands in for the password, not the second factor.
      if (response.mfa_required) {
        setMfaChallenge({ email: response.therapist.email });
        return;
      }
      setTherapist(response.therapist);
      refreshTherapist();
      if (response.is_new_user) {
        setNewTherapist(response.therapist);
      } else {
        navigate("/");
      }
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setError(t("auth.errorGooglePopupClosed"));
      } else {
        setError(t("auth.errorGoogleGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageDone = (updated: Therapist) => {
    setTherapist(updated);
    setNewTherapist(null);
    navigate("/");
  };

  const label = mode === "signIn" ? t("auth.signInWithGoogle") : t("auth.signUpWithGoogle");

  return (
    <>
      {mfaChallenge && (
        <MfaChallenge email={mfaChallenge.email} onCancel={() => setMfaChallenge(null)} />
      )}
      {newTherapist && <LanguageSetupModal therapist={newTherapist} onDone={handleLanguageDone} />}
      <div className="w-full">
      <Button
        type="button"
        variant="outline"
        className="w-full min-h-[44px]"
        aria-label={label}
        onClick={handleGoogleSignIn}
        loading={loading}
        startIcon={
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" />
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" />
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z" />
          </svg>
        }
      >
        {label}
      </Button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-error-500 text-center">
          {error}
        </p>
      )}
      </div>
    </>
  );
}
